import { useCallback, useEffect, useRef, useState } from 'react';
// @ts-ignore
import { HiOutlineArrowDownTray, HiOutlineExclamationTriangle, HiOutlineEnvelope, HiOutlineXMark, HiOutlineCheckCircle } from 'react-icons/hi2';
import { triggerDesktopDownload } from '../../constants/desktop';
import './DesktopDownloadModal.css';

const SUPPORT_EMAIL = 'jpande@gatekeepersystems.com';

const STEPS: { title: string; desc: string; warn?: string }[] = [
  {
    title: 'Run the installer',
    desc: 'Open atlas-setup.exe from your Downloads folder.',
    warn: 'Windows SmartScreen may appear — click "More info" then "Run anyway" to proceed.',
  },
  {
    title: 'Sign in',
    desc: 'Launch Atlas Desktop and sign in with your Gatekeeper email address.',
  },
  {
    title: 'Open a brand in Claude',
    desc: 'Select any brand from the Opportunity table, then click "Open in Claude" to create an AI workspace with your data pre-loaded.',
  },
];

const TROUBLESHOOTING: { q: string; a: string }[] = [
  {
    q: '"Windows protected your PC" appears when I run the installer',
    a: 'This is Windows SmartScreen — it shows for any newly published app. Click "More info", then "Run anyway". Atlas is safe and signed by Gatekeeper Systems.',
  },
  {
    q: 'My antivirus or browser blocked the download',
    a: 'The installer is a trusted Gatekeeper build. Choose "Keep" / "Allow" if your browser warns you, and add an exception in your antivirus if needed.',
  },
  {
    q: 'The app won\'t open after installing',
    a: 'Atlas Desktop requires 64-bit Windows 10 or later. Restart your machine after install, then launch Atlas from the Start menu. If it still fails, reinstall using the latest download above.',
  },
  {
    q: 'I can\'t sign in',
    a: 'Use your @gatekeepersystems.com email — the same account you use for the web app. If sign-in still fails, contact support below.',
  },
  {
    q: '"Open in Claude" does nothing',
    a: 'Make sure Claude Desktop is installed and you are signed in to it. Then retry "Open in Claude" from any brand.',
  },
];

interface Props {
  onClose: () => void;
}

export default function DesktopDownloadModal({ onClose }: Props) {
  // Browsers give no event for download start/finish, so we optimistically show a
  // "downloading" state for a few seconds, then a "done" state. "Download again"
  // restarts it — useful if the download didn't kick off (popup blocker, etc.).
  const [phase, setPhase] = useState<'downloading' | 'done'>('downloading');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const started = useRef(false);

  const startDownload = useCallback(() => {
    triggerDesktopDownload();
    setPhase('downloading');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setPhase('done'), 5000);
  }, []);

  useEffect(() => {
    if (started.current) return; // guard against React StrictMode double-invoke
    started.current = true;
    startDownload();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [startDownload]);

  return (
    <div className="ddm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ddm-dialog">
        <button className="ddm-close" onClick={onClose} aria-label="Close">
          <HiOutlineXMark />
        </button>

        <div className="ddm-body">
        {/* Hero + download status */}
        <header className="ddm-hero">
          <h1>Get Atlas Desktop</h1>
          <p>AI-powered brand analysis, directly on your machine.</p>

          <div className="ddm-status">
            {phase === 'downloading' ? (
              <div className="ddm-downloading">
                <span className="ddm-spinner" />
                <span>Downloading Atlas Desktop…</span>
              </div>
            ) : (
              <div className="ddm-done">
                <HiOutlineCheckCircle className="ddm-done-icon" />
                <span>Your download should be ready — find <strong>atlas-setup.exe</strong> in your Downloads folder.</span>
              </div>
            )}

            <button className="ddm-download-btn" onClick={startDownload}>
              <HiOutlineArrowDownTray />
              Download again
              <span className="ddm-platform-tag">Windows</span>
            </button>
            <span className="ddm-hint">If the download didn’t start automatically, use this button.</span>
          </div>
          <span className="ddm-note">64-bit Windows 10 or later · Free for Gatekeeper users</span>
        </header>

        {/* Video guide — removed for now; re-add the install walkthrough here later. */}

        {/* Installation steps */}
        <section className="ddm-section">
          <h2>Installation steps</h2>
          <div className="ddm-steps">
            {STEPS.map((step, i) => (
              <div className="ddm-step" key={i}>
                <div className="ddm-step-num">{i + 1}</div>
                <div className="ddm-step-body">
                  <div className="ddm-step-title">{step.title}</div>
                  <div className="ddm-step-desc">{step.desc}</div>
                  {step.warn && (
                    <div className="ddm-step-warn">
                      <HiOutlineExclamationTriangle />
                      {step.warn}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="ddm-section">
          <h2>Troubleshooting</h2>
          <div className="ddm-faq">
            {TROUBLESHOOTING.map((item, i) => (
              <details className="ddm-faq-item" key={i}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Support */}
        <section className="ddm-support">
          <HiOutlineEnvelope className="ddm-support-icon" />
          <div>
            <div className="ddm-support-title">Still need help?</div>
            <div className="ddm-support-desc">
              Email our team at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Atlas Desktop Support`}>{SUPPORT_EMAIL}</a>
            </div>
          </div>
        </section>
        </div>
      </div>
    </div>
  );
}
